import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import Countdown from "./Countdown";

export default function Pricing({ onBuy }) {
  const { t } = useI18n();
  return (
    <section
      id="pricing"
      className="relative py-24 md:py-32"
      style={{ background: "var(--cream)" }}
      data-testid="pricing-section"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-[1fr_1.15fr] gap-10 lg:gap-16 items-center">
          <div>
            <div className="chip-num mb-4">{t.pricing.chip}</div>
            <h2 className="font-display font-medium text-[2.6rem] sm:text-[3rem] lg:text-[4rem] leading-[0.98] tracking-[-0.03em] text-[var(--ink)]">
              {t.pricing.h2_a} <br />
              <span className="font-editorial-italic text-[var(--marigold)]">
                {t.pricing.h2_italic}
              </span>
            </h2>
            <p className="mt-6 text-[var(--muted)] text-base md:text-lg leading-relaxed max-w-[46ch]">
              {t.pricing.p}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-[var(--muted)]">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[var(--jade)]" />
                {t.pricing.trust1}
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[var(--jade)]" />
                {t.pricing.trust2}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative rounded-[28px] overflow-hidden bg-[var(--ink)] text-[var(--cream)] p-8 md:p-10"
          >
            <div
              className="absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full bg-[var(--marigold)] opacity-[0.18] blur-3xl"
              aria-hidden="true"
            />
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="font-mono-caption text-[11px] tracking-[0.25em] uppercase opacity-70">
                  {t.pricing.strip_left}
                </div>
                <div className="rounded-full border border-[var(--marigold)] text-[var(--marigold-2)] px-3 py-1 font-mono-caption text-[10px] tracking-[0.2em] uppercase">
                  {t.pricing.strip_right}
                </div>
              </div>

              <div className="mt-6 flex items-end gap-4">
                <div className="font-display text-[7rem] md:text-[9rem] leading-none tracking-[-0.05em]">
                  $3
                </div>
                <div className="pb-4">
                  <div className="text-[var(--cream)]/50 line-through font-display text-2xl">$29</div>
                  <div className="font-mono-caption text-[10px] uppercase tracking-[0.2em] opacity-70">
                    {t.pricing.currency_note}
                  </div>
                </div>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {t.pricing.includes.map((f, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-[14.5px]">
                    <span className="mt-1.5 shrink-0">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M5 12l4 4L19 6"
                          stroke="var(--marigold-2)"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                    <span className="text-[var(--cream)]/85 leading-snug">{f}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Countdown />
              </div>

              <button
                onClick={onBuy}
                className="btn-pill btn-primary w-full mt-5 py-4 text-base"
                data-testid="pricing-buy-button"
              >
                <span>{t.pricing.cta}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>

              <div className="mt-4 text-center text-[12px] text-[var(--cream)]/55">
                {t.pricing.after_cta}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
